import { useCallback, useEffect, useRef, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import axios from "axios";
import {
  FiDownload,
  FiMapPin,
  FiTruck,
} from "react-icons/fi";
import { FaApple, FaFacebookF, FaInstagram, FaYoutube } from "react-icons/fa";
import { fetchPublicInvoice, getInvoiceApiUrl } from "../utils/invoiceApi";

const formatDate = (value) => {
  const date = new Date(value || Date.now());
  if (Number.isNaN(date.getTime())) return "—";
  return date.toLocaleString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  });
};

const money = (value) =>
  `₹${Math.round(Number(value) || 0).toLocaleString("en-IN")}`;

const paymentSummary = (method, amount) => {
  const normalizedMethod = String(method || "").trim().toLowerCase();
  const label =
    normalizedMethod === "online"
      ? "Online"
      : normalizedMethod === "wallet"
        ? "Wallet"
        : normalizedMethod === "cod"
          ? "COD"
          : method || "—";
  return `${label} • ${money(amount)}`;
};

const customerAppUrl =
  "https://play.google.com/store/apps/details?id=com.instaport.customer";
const customerIosAppUrl =
  "https://apps.apple.com/in/app/instaport-delivery/id6751334012";
const socialLinks = [
  {
    label: "YouTube",
    href: "https://www.youtube.com/@instaportdelivery",
    Icon: FaYoutube,
    className: "text-red-600",
  },
  {
    label: "Facebook",
    href: "https://www.facebook.com/people/instaportdelivery/61590141218960/",
    Icon: FaFacebookF,
    className: "text-blue-600",
    iconClassName: "translate-x-px",
  },
  {
    label: "Instagram",
    href: "https://www.instagram.com/instaportdelivery",
    Icon: FaInstagram,
    className: "text-pink-500",
  },
];

const Info = ({ label, value, success = false }) => (
  <div className="min-w-0 min-h-[48px]">
    <p className="text-[10px] font-bold tracking-[0.1em] text-[#798497]">
      {label.toUpperCase()}
    </p>
    <p
      className={`mt-1 break-words text-sm font-semibold leading-5 ${
        success ? "text-[#138536]" : "text-[#263348]"
      }`}
    >
      {value || "—"}
    </p>
  </div>
);

const Address = ({ title, address, green = false }) => (
  <div className="flex gap-3">
    <span
      className={`mt-1 flex h-9 w-9 shrink-0 items-center justify-center rounded-xl ${
        green ? "bg-[#eaf8ee] text-[#138536]" : "bg-[#fff0f1] text-[#de4c56]"
      }`}
    >
      {green ? <FiTruck /> : <FiMapPin />}
    </span>
    <div className="min-w-0 flex-1">
      <p className="text-[10px] font-bold tracking-[0.1em] text-[#798497]">
        {title.toUpperCase()}
      </p>
      <p className="mt-1 break-words text-sm font-semibold leading-6 text-[#263348]">
        {address || "—"}
      </p>
    </div>
  </div>
);

const Invoice = () => {
  const { orderId } = useParams();
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token") || "";
  const [invoice, setInvoice] = useState(null);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(false);
  const [error, setError] = useState("");
  const hasAutoDownloadedRef = useRef(false);
  const [apiUrl, setApiUrl] = useState(getInvoiceApiUrl);

  const downloadInvoice = useCallback(async () => {
    if (!invoice?.apiDownloadUrl || downloading) return;
    setDownloading(true);
    try {
      const response = await axios.get(`${apiUrl}${invoice.apiDownloadUrl}`, {
        responseType: "blob",
        timeout: 30000,
      });
      const url = window.URL.createObjectURL(
        new Blob([response.data], { type: "application/pdf" })
      );
      const link = document.createElement("a");
      link.href = url;
      link.download = `${invoice.invoiceNumber || "Instaport-Invoice"}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.setTimeout(() => window.URL.revokeObjectURL(url), 1000);
    } catch (_) {
      setError("The invoice PDF could not be downloaded. Please try again.");
    } finally {
      setDownloading(false);
    }
  }, [apiUrl, downloading, invoice]);

  useEffect(() => {
    let active = true;
    const controller = new AbortController();
    setInvoice(null);
    setError("");
    setLoading(true);
    hasAutoDownloadedRef.current = false;
    if (!orderId || !token) {
      setError("This invoice link is invalid.");
      setLoading(false);
      return undefined;
    }

    fetchPublicInvoice(orderId, token, { signal: controller.signal })
      .then((result) => {
        if (!active) return;
        setApiUrl(result.apiUrl);
        setInvoice(result.invoice);
      })
      .catch((requestError) => {
        if (active) {
          setError(
            requestError.response?.data?.message ||
              requestError.message ||
              "Invoice not found."
          );
        }
      })
      .finally(() => {
        if (active) setLoading(false);
      });

    return () => {
      active = false;
      controller.abort();
    };
  }, [orderId, token]);

  useEffect(() => {
    if (
      searchParams.get("download") === "1" &&
      invoice &&
      !downloading &&
      !hasAutoDownloadedRef.current
    ) {
      hasAutoDownloadedRef.current = true;
      downloadInvoice();
    }
  }, [downloadInvoice, downloading, invoice, searchParams]);

  if (loading) {
    return (
      <main className="min-h-screen bg-[#f5f7fa] px-4 py-16">
        <div className="mx-auto flex max-w-xl flex-col items-center rounded-3xl bg-white px-8 py-16 text-center shadow-[0_22px_60px_rgba(15,23,42,0.12)]">
          <span className="h-11 w-11 animate-spin rounded-full border-4 border-[#f7cf77] border-t-[#f5a000]" />
          <h1 className="mt-5 text-xl font-bold text-[#18253a]">
            Opening your invoice
          </h1>
          <p className="mt-2 text-sm text-[#657286]">Please wait a moment.</p>
        </div>
      </main>
    );
  }

  if (!invoice) {
    return (
      <main className="min-h-screen bg-[#f5f7fa] px-4 py-16">
        <div className="mx-auto max-w-xl rounded-3xl bg-white px-8 py-16 text-center shadow-[0_22px_60px_rgba(15,23,42,0.12)]">
          <h1 className="text-xl font-bold text-[#18253a]">Invoice unavailable</h1>
          <p className="mt-3 text-sm leading-6 text-[#657286]">
            {error || "This invoice link is no longer available."}
          </p>
        </div>
      </main>
    );
  }

  const drops = Array.isArray(invoice.drops) ? invoice.drops : [];
  const lastDrop = drops[drops.length - 1];
  const additionalDropCount = Math.max(
    (invoice.manualRoute?.dropCount || drops.length) - 1, 0);
  return (
    <main className="min-h-screen bg-[#f5f7fa] px-3 py-5 sm:px-6 sm:py-10">
      <section className="mx-auto max-w-[900px] overflow-hidden rounded-[28px] bg-white shadow-[0_22px_60px_rgba(15,23,42,0.13)]">
        <div className="h-2 bg-[#f5a000]" />
        <div className="p-5 sm:p-9">
          <header className="flex flex-col gap-5 border-b border-[#e7ecf2] pb-6 sm:flex-row sm:items-start sm:justify-between">
            <div className="min-w-0">
              <p className="text-2xl font-extrabold tracking-tight sm:text-3xl">
                <span className="text-[#117b39]">INSTAPORT</span>{" "}
                <span className="text-[#f5a000]">DELIVERY</span>
              </p>
              <p className="mt-1 text-[11px] font-bold tracking-[0.13em] text-[#6d7889]">
                COMPLETED ORDER INVOICE
              </p>
            </div>
            <div className="sm:text-right">
              <h1 className="text-xl font-bold text-[#1b283c]">Delivery Invoice</h1>
              <p className="mt-1 text-sm font-semibold text-[#4e5b70]">
                Order No. {invoice.orderNumber}
              </p>
              <p className="mt-1 text-xs text-[#718096]">
                Delivered {formatDate(invoice.deliveredAt)}
              </p>
            </div>
          </header>

          <div className="mt-5 grid gap-4 md:grid-cols-2">
            <section className="rounded-2xl border border-[#e6edf1] bg-[#f9fbfc] p-4">
              <p className="text-[11px] font-bold tracking-[0.12em] text-[#117b39]">
                CUSTOMER & PACKAGE
              </p>
              <div className="mt-4 grid auto-rows-fr gap-x-5 gap-y-5 sm:grid-cols-2">
                <Info label="Consignor" value={invoice.customer?.name} />
                <Info label="Phone" value={invoice.customer?.phone} />
                <Info
                  label="Package"
                  value={`${invoice.packageType} • ${invoice.parcelWeight}`}
                />
                <Info label="Declared value" value={money(invoice.parcelValue)} />
              </div>
            </section>
            <section className="rounded-2xl border border-[#e6edf1] bg-[#f9fbfc] p-4">
              <p className="text-[11px] font-bold tracking-[0.12em] text-[#117b39]">
                ORDER & RIDER
              </p>
              <div className="mt-4 grid auto-rows-fr gap-x-5 gap-y-5 sm:grid-cols-2">
                <Info label="Order status" value="Delivered" success />
                <Info
                  label="Total paid"
                  value={paymentSummary(invoice.paymentMethod, invoice.amount)}
                />
                <Info label="Rider" value={invoice.rider?.name} />
                <Info label="Bike number" value={invoice.rider?.vehicleNumber} />
              </div>
            </section>
          </div>

          <section className="mt-4 rounded-2xl border border-[#e6edf1] p-4 sm:p-5">
            <p className="text-[11px] font-bold tracking-[0.12em] text-[#117b39]">
              DELIVERY ROUTE
            </p>
            <div className="mt-4 flex flex-col">
              <Address
                title="Pickup location"
                address={invoice.pickup?.address}
                green
              />
              {invoice.manualRoute?.dropMode === "all" ? (
                invoice.drops.map((drop, index) => (
                  <div key={index} className="mt-5">
                    <Address title={drop.label} address={drop.address} />
                  </div>
                ))
              ) : (
                <>
                  <div className="relative flex h-12 items-center justify-center">
                    <span className="absolute bottom-0 left-[17px] top-0 border-l-2 border-dotted border-[#b8c4d0]" />
                    {additionalDropCount > 0 && (
                      <span className="relative z-10 rounded-full bg-[#fff4d6] px-2.5 py-1 text-xs font-bold text-[#a56b00]">
                        +{additionalDropCount} {additionalDropCount === 1 ? "drop" : "drops"}
                      </span>
                    )}
                  </div>
                  <Address
                    title={lastDrop?.label || "Drop location"}
                    address={lastDrop?.address}
                  />
                </>
              )}
            </div>
          </section>

          <footer className="mt-5 grid gap-4 rounded-2xl bg-[#f7f8fb] p-4 sm:grid-cols-[1fr_auto] sm:items-end sm:p-5">
            <div>
              <h2 className="font-bold text-[#273449]">{invoice.company?.name}</h2>
              <p className="mt-1 max-w-2xl text-xs leading-5 text-[#69768a]">
                {invoice.company?.address}
              </p>
              <p className="mt-2 text-xs text-[#69768a]">
                {invoice.company?.phone} • {invoice.company?.email} • GSTIN: {invoice.company?.gst}
              </p>
              <p className="mt-4 text-xs leading-5 text-[#718096]">
                This digitally generated invoice confirms the completed Instaport
                order shown above. Please retain it for your records.
              </p>
            </div>
            <div className="flex flex-col items-start gap-4 sm:items-end">
              <div className="sm:text-right">
                <p className="text-xs font-bold uppercase tracking-[0.1em] text-[#4e5b70]">
                  Download the app now
                </p>
                <div className="mt-2 flex gap-4">
                  <a
                    href={customerAppUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label="Download Instaport Delivery for Android"
                    className="transform transition hover:scale-105"
                  >
                    <img
                      src="/assets/footer/playstore.png"
                      alt="Android app"
                      className="h-12"
                    />
                  </a>
                  <a
                    href={customerIosAppUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label="Download Instaport Delivery for iOS"
                    className="flex h-12 w-12 items-center justify-center rounded-full bg-[#4389cf] text-white shadow-sm transition hover:scale-105"
                  >
                    <FaApple className="h-8 w-8" aria-hidden="true" />
                  </a>
                </div>
                <div className="mt-4 flex items-center gap-3 text-xl">
                  {socialLinks.map(({ label, href, Icon, className, iconClassName = "" }) => (
                    <a
                      key={label}
                      href={href}
                      target="_blank"
                      rel="noopener noreferrer"
                      aria-label={`Follow Instaport Delivery on ${label}`}
                      className={`flex h-11 w-11 items-center justify-center rounded-full bg-white shadow-md transition hover:-translate-y-0.5 hover:shadow-xl ${className}`}
                    >
                      <Icon className={iconClassName} />
                    </a>
                  ))}
                </div>
              </div>
              <button
                type="button"
                onClick={downloadInvoice}
                disabled={downloading}
                className="inline-flex min-h-12 items-center justify-center gap-2 rounded-xl bg-[#f5a000] px-5 py-3 text-sm font-bold text-white shadow-[0_12px_24px_rgba(245,160,0,0.23)] transition hover:brightness-105 disabled:cursor-wait disabled:opacity-70"
              >
                {downloading ? (
                  <span className="h-4 w-4 animate-spin rounded-full border-2 border-white/35 border-t-white" />
                ) : (
                  <FiDownload />
                )}
                {downloading ? "Generating invoice..." : "Download Invoice"}
              </button>
            </div>
          </footer>
          {error && <p className="mt-4 text-center text-sm text-[#b42318]">{error}</p>}
        </div>
      </section>
    </main>
  );
};

export default Invoice;
